import { useSignal } from '@preact/signals-react';
import { Box, Stack } from '@mui/material';
import { Button, CodeEditor, List } from '../../../resolvers-types';
import NGLayoutItem from '../../generators/NGLayoutItem';
import { setupLocalState } from '../../library/dataService';
import { INGServicesEditorProps } from '../../library/NGFieldExtensions'
import CustomSwitch from '../NGCustomSwitch/NGCustomSwitch';
import NGCodeEditor from '../NGCodeEditor/NGCodeEditor';

import NGList from '../NGList/NGList';
import Icon from '../NGMaterialIcon/NGMaterialIcon';

const deleteButton = {
  __typename: "Button",
  Id: "1",
  Title: "Delete",
  TopIcon: { IconName: "Delete" },
  Classes: ["button-tertiary-small"],
  Actions: [
    {
      Trigger: "onClick",
      CommandSet: {
        FirstCommandId: "1",
        ExecuteCommandsInParallel: false,
        Commands: [
          {
            Id: "1",
            Instruction: {
              Name: "ShowMessage",
            },
            Parameters: [
              { Name: "Title", Value: "Delete Asset" },
              {
                Name: "Message",
                Value: "Are you sure you want to delete this asset?",
              },
              { Name: "ShowOkButton", Value: true },
              { Name: "ShowCancelButton", Value: true },
            ],
            NextCommandIdOnSuccess: "2",
          },
          {
            Id: "2",
            Instruction: {
              Name: "SetState",
            },
            Parameters: [{
              Name: "Bindings",
              Value: {
                ["State.NGForm.NGPropEditor.Services"]: "State.NGForm.NGPropEditor.Services.filter( function(x) { x.Id !== Row.Id })",
              }
            }],
            NextCommandIdOnSuccess: "3",
          },
          {
            Id: "3",
            Instruction: {
              Name: "SetState",
            },
            Parameters: [{
              Name: "Bindings",
              Value: {
                "State.PropertiesEditorData.Services": "State.NGForm.NGPropEditor.Services",
              }
            }],
            NextCommandIdOnSuccess: "4",
          },
          {
            Id: "4",
            Instruction: {
              Name: "TriggerAction",
            },
            Parameters: [
              {
                Name: "Trigger",
                Value: "onSave",
              },
              {
                Name: "TargetId",
                Value: "PropertiesEditor/A/1",
              },
              {
                Name: "Form",
                Value: "NGPropEditor",
              },
            ],
          },
        ],
      },
    },
  ],
};
const editButton = {
  __typename: "Button",
  Id: "2",
  TopIcon: { IconName: "Edit" },
  Title: "Edit",
  Classes: ["button-tertiary-small"],
  Actions: [
    {
      Trigger: "onClick",
      CommandSet: {
        FirstCommandId: "0",
        ExecuteCommandsInParallel: false,
        Commands: [
          {
            Id: "0",
            Instruction: {
              Name: "ClearComponentState",
            },
            Parameters: [
              {
                Name: "ComponentId",
                Value: "test-harness-v3-TestHarnessAsServiceEditorPopupReference"
              }
            ],
            NextCommandIdOnSuccess: "1",
          },
          {
            Id: "1",
            Instruction: {
              Name: "SetState",
            },
            Parameters: [{
              Name: "Bindings",
              Value: {
                "State.SelectedService": "clone(State.SelectedItem.Services.find(function(s){ s.Name === Row.Name }))",
                "State.CopyOfSelectedService": "clone(State.SelectedItem.Services.find(function(s){ s.Name === Row.Name }))",
                "State.ServiceIndex": "State.SelectedItem.Services.findIndex(function(s){ s.Name === Row.Name })",
                "State.ServiceEditorEditMode": "true",
              }
            }],
            NextCommandIdOnSuccess: "2",
          },
          {
            Id: "2",
            Instruction: {
              Name: "SetState",
            },
            Parameters: [{
              Name: "Bindings",
              Value: {
                "State.ResultBindingsTypesData": "generateBindingsTypeData(clone(State.SelectedService))",
                "State.OpenServiceProperties": "true",
              }
            }],
            NextCommandIdOnSuccess: "3",
          },
          {
            Id: "3",
            Instruction: {
              Name: "OpenModalPopup",
            },
            Parameters: [
              { Name: "ModalPopupId", Value: "ServiceEditorPopup" },
              { Name: "Title", Value: "Test Harness as Service Editor" },
            ],
          },
        ],
      },
    },
  ]
};
const addButton = {
  __typename: "Button",
  Id: "2add",
  TopIcon: { IconName: "Add" },
  Title: "Add",
  Classes: ["button-tertiary-small"],
  Actions: [
    {
      Trigger: "onClick",
      CommandSet: {
        FirstCommandId: "0",
        ExecuteCommandsInParallel: false,
        Commands: [
          {
            Id: "0",
            Instruction: {
              Name: "ClearComponentState",
            },
            Parameters: [
              {
                Name: "ComponentId",
                Value: "test-harness-v3-TestHarnessAsServiceEditorPopupReference"
              }
            ],
            NextCommandIdOnSuccess: "1",
          },
          {
            Id: "1",
            Instruction: {
              Name: "SetState",
            },
            Parameters: [{
              Name: "Bindings",
              Value: {
                "State.SelectedService": "{}",
                "State.ServiceIndex": "State.SelectedItem.Services.length",
                "State.ServiceEditorEditMode": "false",
              }
            }],
            NextCommandIdOnSuccess: "2",
          },
          {
            Id: "2",
            Instruction: {
              Name: "SetState",
            },
            Parameters: [{
              Name: "Bindings",
              Value: {
                "State.ResultBindingsTypesData": "generateBindingsTypeData(clone(State.SelectedService))",
                "State.OpenServiceProperties": "true"
              }
            }],
            NextCommandIdOnSuccess: "3",
          },
          {
            Id: "3",
            Instruction: {
              Name: "OpenModalPopup",
            },
            Parameters: [
              { Name: "ModalPopupId", Value: "ServiceEditorPopup" },
              { Name: "Title", Value: "Test Harness as Service Editor" },
            ],
          },
        ],
      },
    },
  ]
};
const actionsContainer = {
  __typename: "SimpleContainer",
  Style: {
    flexDirection: "row",
  },
  Items: [editButton, deleteButton],
};
const serviceColumns = [
  {
    __typename: "ListColumn",
    Name: "Id",
    Id: "Id",
    Editable: false,
    Visible: false,
    Type: "string",
  },
  {
    __typename: "ListColumn",
    Id: "Name",
    Name: "Name",
    Bindings: {
      Value: "Row.Name || Row.Service",
    },
    Editable: true,
    Visible: true,
    Type: "string",
  },
  {
    __typename: "ListColumn",
    Id: "Trigger",
    Name: "Trigger",
    Editable: true,
    Visible: true,
    Type: "string",
  },
  {
    __typename: "ListColumn",
    Id: "Actions",
    Name: "Actions",
    Editable: false,
    Visible: true,
    Type: "string",
    CellRendererItems: [actionsContainer],
  }
];

export default function NGServicesEditor({ config, context }: INGServicesEditorProps) {
  const local = setupLocalState(
    config,
    {
      Rows: useSignal(config.Rows ?? []),
      Value: useSignal(config.Value ?? []),
    },
    context
  );

  const list = {
    __typename: "List",
    GridHeight: "250px",
    RowsPerPage: 10,
    Id: `${config.Id}-list`,
    UniqueName: `${config.Id}-list`,
    ListColumns: serviceColumns,
    Actions: [
      {
        Trigger: "onRowUpdate",
        CommandSet: {
          FirstCommandId: "1",
          ExecuteCommandsInParallel: false,
          Commands: [
            {
              Id: "1",
              Instruction: {
                Name: "SetState",
              },
              Parameters: [{
                Name: "Bindings",
                Value: {
                  "State.PropertiesEditorData.Services": "Event.Rows",
                }
              }],
              NextCommandIdOnSuccess: "2",
            },
            {
              Id: "2",
              Instruction: {
                Name: "TriggerAction",
              },
              Parameters: [
                {
                  Name: "Trigger",
                  Value: "onSave",
                },
                {
                  Name: "TargetId",
                  Value: "PropertiesEditor/A/1",
                },
                {
                  Name: "Form",
                  Value: "NGPropEditor",
                },
              ],
            }
          ],
        },
      },
    ],
    Bindings: {
      Rows: config.Bindings?.Rows || JSON.stringify(local.Rows.value),
    },
    Toolbar: {
      ShowExport: false,
      ShowFilter: false,
    },
    SpreadsheetMode: true,
    SpreadsheetModeOptions: {
      Mode: "Excel",
      ShowAddColumnsButton: false,
      ShowAddRowsButton: false,
    },
    Visible: true,
    ShowClearAllFilters: false,
    Pagination: false,
    ShowFooter: false,
    HideFooterPagination: true,
    GridLayout: "normal",
    EditMode: "fullRow",
  } as List;

  const showCodeEditor = useSignal(false);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: "0.6rem" }}>
      <Stack direction="row" justifyContent="flex-end" gap="1rem" alignItems="center" sx={{ transform: "translateY(75%)" }}>
        <CustomSwitch
          checked={showCodeEditor.value}
          onChange={() => {
            showCodeEditor.value = !showCodeEditor.value;
          }}
          icon={<Icon name="code" />}
          checkedIcon={<Icon name="code" />}
          sx={{
            alignSelf: "flex-end",
            marginBottom: "3px",
          }}
        />
        <NGLayoutItem
          config={addButton as Button}
          context={context}
        />
      </Stack>
      {showCodeEditor.value ? (
        <Box sx={{ width: "100%", marginTop: "1rem" }}>
          <NGCodeEditor config={{ ...config, Height: "200px", __typename: "CodeEditor" } as CodeEditor} context={context} />
        </Box>
      ) : (
        <NGList config={list} context={context} />
      )}
    </Box>
  )
}
